<template>
  <div class="flex flex-col gap-4 p-1">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium">Type <span class="text-red-500">*</span></label>
      <SelectButton v-model="form.type" :options="typeOptions" option-label="label" option-value="value" fluid />
      <small v-if="errors.type" class="text-red-500">{{ errors.type[0] }}</small>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium">Date <span class="text-red-500">*</span></label>
      <DatePicker v-model="form.date" inline :min-date="new Date()" date-format="dd.mm.yy" />
      <small v-if="errors.start_date" class="text-red-500">{{ errors.start_date[0] }}</small>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium">Time <span class="text-red-500">*</span></label>
      <DatePicker
        v-model="form.time"
        time-only
        hour-format="24"
        fluid
        placeholder="HH:MM"
        show-icon
        icon="pi pi-clock"
      />
      <small v-if="errors.time" class="text-red-500">{{ errors.time[0] }}</small>
    </div>

    <FormGroup name="note" id="reminder-note" label="Note" :validation-errors="errors">
      <Textarea v-model="form.note" rows="3" fluid auto-resize />
    </FormGroup>

    <div class="flex justify-between gap-2 mt-2">
      <Button
        v-if="reminder"
        label="Delete"
        icon="pi pi-trash"
        severity="danger"
        outlined
        size="small"
        :loading="deleting"
        @click="onDelete"
      />
      <Button
        :label="reminder ? 'Update' : 'Save'"
        icon="pi pi-check"
        size="small"
        class="ml-auto"
        :loading="saving"
        @click="onSubmit"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Button, DatePicker, SelectButton, Textarea } from 'primevue'
import FormGroup from '@/components/admin/form/FormGroup.vue'
import apiClient from '@/utils/apiClient'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  customerId: { type: Number, required: true },
  reminder: { type: Object, default: null },
  preselectedDate: { type: Date, default: null },
})

const emit = defineEmits(['saved', 'deleted'])

const toast = useToast()

const now = new Date()
const typeOptions = [
  { label: 'Phone', value: 'phone_call' },
  { label: 'Email', value: 'email' },
  { label: 'Note', value: 'note' },
]

const parseInitialDate = () => {
  if (!props.reminder?.startDate) return null
  return new Date(props.reminder.startDate)
}

const parseInitialTime = () => {
  if (!props.reminder?.startDate) return null
  const d = new Date(props.reminder.startDate)
  const t = new Date()
  t.setHours(d.getHours(), d.getMinutes(), 0, 0)
  return t
}

const form = ref({
  type: props.reminder?.type ?? 'phone_call',
  date: parseInitialDate() ?? props.preselectedDate ?? now,
  time: parseInitialTime() ?? now,
  note: props.reminder?.note ?? '',
})

const errors = ref({})
const saving = ref(false)
const deleting = ref(false)

const buildDateTime = () => {
  if (!form.value.date) return null

  const d = new Date(form.value.date)
  const h = form.value.time ? form.value.time.getHours() : 0
  const m = form.value.time ? form.value.time.getMinutes() : 0

  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(h).padStart(2, '0')
  const minutes = String(m).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:00`
}

const onSubmit = async () => {
  errors.value = {}

  if (!form.value.type) errors.value.type = ['Type is required.']
  if (!form.value.date) errors.value.start_date = ['Date is required.']
  if (!form.value.time) errors.value.time = ['Time is required.']

  if (Object.keys(errors.value).length) return

  saving.value = true

  const payload = {
    customer_id: props.customerId,
    type: form.value.type,
    start_date: buildDateTime(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    note: form.value.note || null,
  }

  try {
    if (props.reminder) {
      const res = await apiClient.put(`/user-reminders/${props.reminder.id}`, payload)
      emit('saved', res.data.data)
      toast.add({ severity: 'success', summary: 'Updated', detail: 'Reminder updated.', life: 3000 })
    } else {
      const res = await apiClient.post('/user-reminders', payload)
      emit('saved', res.data.data)
      toast.add({ severity: 'success', summary: 'Created', detail: 'Reminder created.', life: 3000 })
    }
  } catch (error) {
    if (error.response?.status === 422) {
      errors.value = error.response.data.errors
      return
    }
    toast.add({ severity: 'error', summary: 'Error', detail: 'Something went wrong.', life: 3000 })
  } finally {
    saving.value = false
  }
}

const onDelete = async () => {
  deleting.value = true
  try {
    await apiClient.delete(`/user-reminders/${props.reminder.id}`)
    emit('deleted', props.reminder.id)
    toast.add({ severity: 'success', summary: 'Deleted', detail: 'Reminder deleted.', life: 3000 })
  } catch {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete.', life: 3000 })
  } finally {
    deleting.value = false
  }
}
</script>
